import { useState } from "react";
import { ClipboardList, Package, PenSquare } from "lucide-react";

import AdminLogin from "@/components/AdminLogin";
import AuthGateFallback from "@/components/AuthGateFallback";
import ConfirmDialog from "@/components/ConfirmDialog";
import OrderForm from "@/components/OrderForm";
import HomeOverviewPanel, { type HomeQueueFilter } from "@/components/dashboard/HomeOverviewPanel";
import OrderQueueWorkstream from "@/features/orders/OrderQueueWorkstream";
import { useAuth } from "@/hooks/useAuth";
import type { OrderStatusV2 } from "@/lib/types";

type WorkspaceMode = "queue" | "create";
const HOMEPAGE_QUEUE_STATUSES: OrderStatusV2[] = ["pending", "preparing", "ready_to_ship"];

export default function HomePage() {
  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    message: "",
    onConfirm: () => {},
  });
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceMode>("queue");
  const [queueFilter, setQueueFilter] = useState<HomeQueueFilter>("all");
  const queueStatuses: OrderStatusV2[] =
    queueFilter === "all" ? HOMEPAGE_QUEUE_STATUSES : [queueFilter];

  const { isAuthenticated, isReady, hasRole } = useAuth();
  const canManageOrders = hasRole("admin", "staff");

  const showConfirmDialog = (message: string, onConfirm: () => void) => {
    setConfirmConfig({
      isOpen: true,
      message,
      onConfirm,
    });
  };

  const hideConfirmDialog = () => {
    setConfirmConfig((current) => ({
      ...current,
      isOpen: false,
    }));
  };

  const openWorkspace = (workspace: WorkspaceMode) => {
    setActiveWorkspace(workspace);
    requestAnimationFrame(() => {
      document.getElementById("home-workspace")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  if (!isReady) {
    return <AuthGateFallback />;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-canvas p-4">
        <div className="flex w-full max-w-xl flex-col items-center">
          <div className="mb-8 flex items-center justify-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-action shadow-sm">
              <Package className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-ink-strong">訂單作業中心</h1>
          </div>

          <AdminLogin />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas font-sans text-ink">
      <header className="glass-header sticky top-0 z-40 flex w-full flex-col items-stretch justify-between gap-3 px-4 py-3 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-canvas/80 sm:flex-row sm:items-center sm:px-6">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-action shadow-sm">
            <Package className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-strong">訂單作業中心</h1>
          {hasRole("admin") && (
            <a
              href={import.meta.env.VITE_SPREADSHEET_PUBLIC_URL || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden rounded-full border border-line-strong bg-surface/60 px-3 py-1 text-xs font-medium text-progress transition-colors hover:border-progress/40 hover:text-action-hover lg:inline-flex"
              data-testid="link-price-source"
            >
              開啟價目表來源
            </a>
          )}
        </div>

        <AdminLogin />
      </header>

      <main className="mx-auto flex w-full max-w-[1500px] flex-1 flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <HomeOverviewPanel queueFilter={queueFilter} onQueueFilterChange={setQueueFilter} />

        {canManageOrders ? (
          <section id="home-workspace" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-full border border-line bg-surface/70 p-1">
                <button
                  type="button"
                  data-testid="workspaceTabQueue"
                  onClick={() => openWorkspace("queue")}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeWorkspace === "queue"
                      ? "bg-action text-white"
                      : "text-ink hover:bg-raised hover:text-ink"
                  }`}
                >
                  <ClipboardList className="h-4 w-4" />
                  工作清單
                </button>
                <button
                  type="button"
                  data-testid="workspaceTabCreate"
                  onClick={() => openWorkspace("create")}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeWorkspace === "create"
                      ? "bg-action text-white"
                      : "text-ink hover:bg-raised hover:text-ink"
                  }`}
                >
                  <PenSquare className="h-4 w-4" />
                  新增訂單
                </button>
              </div>
            </div>

            {activeWorkspace === "queue" ? (
              <section id="home-order-queue" className="scroll-mt-24">
                <OrderQueueWorkstream
                  showConfirmDialog={showConfirmDialog}
                  showSummaryCards={false}
                  visibleStatuses={queueStatuses}
                />
              </section>
            ) : (
              <section id="home-order-form" className="scroll-mt-24">
                <OrderForm onCreated={() => openWorkspace("queue")} />
              </section>
            )}
          </section>
        ) : (
          <section id="home-workspace" className="scroll-mt-24">
            <OrderQueueWorkstream
              showConfirmDialog={showConfirmDialog}
              showSummaryCards={false}
              visibleStatuses={queueStatuses}
            />
          </section>
        )}

        <ConfirmDialog
          isOpen={confirmConfig.isOpen}
          message={confirmConfig.message}
          onConfirm={() => {
            confirmConfig.onConfirm();
            hideConfirmDialog();
          }}
          onCancel={hideConfirmDialog}
        />
      </main>
    </div>
  );
}
